import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  Typography,
} from '@customink/pigment-react';
import type { TaxExemptionStatus } from '@customink/project-service-types';
import { TAX_EXEMPTION_STATUSES } from '@customink/project-service-types';
import CircularProgress from '@mui/material/CircularProgress';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { PropagateLoader } from 'react-spinners';

import { AvalaraEcmPlugin } from './AvalaraEcmPlugin';
import { ErrorLoadingAvalaraAlert } from './ErrorLoadingAvalaraAlert';
import type { GenCertCertificate } from './genCert';
import { VerificationMethodCard } from './VerificationMethodCard';
import { rollbar } from '../../../config/rollbar.config';
import { useCreateOrGetEcommerceToken } from '../../../hooks/Accounts/TaxExemptionECM/useCreateOrGetEcommerceToken';
import useScript from '../../../hooks/useScript';
import { themeColor } from '../../../utils/designTokens';
import { ChatWithUs } from '../ChatWithUS';
import { TAX_EXEMPTION_VERIFICATION_METHODS } from '../TaxExemptionLineItem';
import type { BusinessAddressFormData } from '../types';

import { googleAnalyticsTag } from '@/components/vendors/googleAnalytics';
import { useGetCustomer } from '@/hooks/Accounts/TaxExemptionECM/useGetCustomer';
import { useSendTaxExemptionsInstructions } from '@/hooks/Klaviyo/useSendTaxExemptionsInstructions';
import { useUpdateProjectConfigTaxExemption } from '@/hooks/Project';

export const avalaraApiUrl = import.meta.env
  .PROJECT_SERVICE_AVALARA_EXEMPTIONS_API_URL;

interface EmbeddedTaxExemptionDialogProps {
  open: boolean;
  onClose: (hasVerified?: boolean) => void;
  state: string;
  isInvalidState: boolean;
  verificationMethod: string;
  onVerificationMethodChange: (method: string) => void;
  onRefetchTaxExemptionCertificates: () => void;
  projectTaxExemptionStatus?: TaxExemptionStatus;
  havePendingAccountTaxExempt: boolean;
}

export function EmbeddedTaxExemptionDialog({
  open,
  onClose,
  state,
  verificationMethod,
  isInvalidState,
  onVerificationMethodChange,
  onRefetchTaxExemptionCertificates,
  havePendingAccountTaxExempt,
  projectTaxExemptionStatus,
}: EmbeddedTaxExemptionDialogProps) {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [tokenExpired, setTokenExpired] = useState(false);

  const sendTaxExemptionsInstructions = useSendTaxExemptionsInstructions();
  const tokenQuery = useCreateOrGetEcommerceToken();
  const [updateProjectConfigTaxExemption] =
    useUpdateProjectConfigTaxExemption();
  const customer = useGetCustomer();
  const avalaraScript = useScript(`${avalaraApiUrl}/js/gencert.js`);

  const continueButtonLoading = false;

  const { handleSubmit, formState, control } = useForm<BusinessAddressFormData>(
    {
      defaultValues: { organizationName: customer.data?.name },
    },
  );
  const onEventVerificationMethodChange = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>,
      method: string,
    ) => {
      event.preventDefault();
      onVerificationMethodChange(method);
    },
    [],
  );

  useEffect(() => {
    let timeoutHandle: NodeJS.Timeout;
    if (tokenQuery.data?.expirationDate) {
      const expiration = new Date(`${tokenQuery.data.expirationDate}Z`); // Z to mark as UTC
      timeoutHandle = setTimeout(() => {
        setTokenExpired(true);
      }, expiration.getTime() - Date.now());
    }
    return () => clearTimeout(timeoutHandle);
  }, [tokenQuery.data?.expirationDate]);

  const handleContinue = useCallback(async () => {
    if (verificationMethod === TAX_EXEMPTION_VERIFICATION_METHODS.VERIFY_NOW) {
      setStep(1);
      return;
    }
    const result = await sendTaxExemptionsInstructions();
    if (result?.error) {
      rollbar.error('Error sending tax exemption instructions', result.error);
      return;
    }
    await updateProjectConfigTaxExemption(TAX_EXEMPTION_STATUSES.CLAIMED);
    onClose();
  }, [verificationMethod]);

  const handleCertificateUploaded = useCallback(
    async (certificate: GenCertCertificate) => {
      await updateProjectConfigTaxExemption(
        certificate.verified
          ? TAX_EXEMPTION_STATUSES.EXEMPT
          : TAX_EXEMPTION_STATUSES.CLAIMED,
      );
      setStep(2);
      onRefetchTaxExemptionCertificates();
    },
    [],
  );

  const handleDialogClose = () => {
    googleAnalyticsTag({
      event: 'customEvent',
      eventCategory: 'Tax Exempt Modal',
      eventAction: 'Click',
      eventLabel: 'Close',
    });
    onClose();
  };

  const title = useMemo(() => {
    switch (step) {
      case 0:
        return 'Select a Tax Exemption Verification Method';
      case 1:
        return 'Submit Your Tax Exemption Certificate';
      case 2:
        return projectTaxExemptionStatus === TAX_EXEMPTION_STATUSES.EXEMPT
          ? 'Tax Exemption Pending'
          : 'Tax Exemption Verified';
    }
  }, [step, projectTaxExemptionStatus]);

  return (
    <Dialog
      data-testid="apply-tax-exemption-dialog"
      disableRestoreFocus
      onClose={handleDialogClose}
      open={open}
      title={title}
      width="lg"
    >
      <DialogContent>
        <>
          {isInvalidState && (
            <>
              <Alert severity="error">
                The state you selected is not eligible for tax exemption.
              </Alert>
              <Typography paddingTop={2} paragraph>
                Please contact us.
              </Typography>
            </>
          )}
          {havePendingAccountTaxExempt && (
            <Alert severity="info">
              Your account already has a pending certificate for this state.
            </Alert>
          )}
          {!isInvalidState && step === 0 && (
            <form
              autoComplete="organization"
              data-testid="tax-exemptions-form"
              id="tax-exemptions-form"
            >
              <Box sx={{ gap: 3, display: 'grid', my: 2 }}>
                <VerificationMethodCard
                  description="Return to the cart and complete your purchase. We'll email you instructions for verifying tax exemption. Once approved, we'll refund any paid sales tax to your original payment method."
                  onClick={onEventVerificationMethodChange}
                  selected={
                    verificationMethod ===
                    TAX_EXEMPTION_VERIFICATION_METHODS.CONTINUE_WITH_PURCHASE
                  }
                  subtitle="Receive sales tax refund after purchase."
                  title="Continue With Purchase"
                  verificationMethod={
                    TAX_EXEMPTION_VERIFICATION_METHODS.CONTINUE_WITH_PURCHASE
                  }
                />
                <VerificationMethodCard
                  description="Submit your certificate now through our portal and get approved in as little as a few minutes. Approvals are typically instant, but may take up to 3 business days in some states."
                  onClick={onEventVerificationMethodChange}
                  selected={
                    verificationMethod ===
                    TAX_EXEMPTION_VERIFICATION_METHODS.VERIFY_NOW
                  }
                  subtitle="Receive sales tax refund after purchase."
                  title="Verify Now"
                  verificationMethod={
                    TAX_EXEMPTION_VERIFICATION_METHODS.VERIFY_NOW
                  }
                />
              </Box>
              <Box pt={2}>
                <FormControl error={Boolean(formState.errors.confirmation)}>
                  <FormHelperText autoFocus>
                    {formState.errors.confirmation?.message}
                  </FormHelperText>
                  <FormControlLabel
                    control={
                      <Controller
                        control={control}
                        name="confirmation"
                        render={({ field: { onChange, value = false } }) => (
                          <Checkbox
                            checked={value}
                            data-testid="tax-exempt-eligible-confirmation-checkbox"
                            onChange={(e) => onChange(e.target.checked)}
                          />
                        )}
                        rules={{
                          required:
                            'Please confirm that this purchase is eligible for tax exemption.',
                        }}
                      />
                    }
                    label="I confirm this purchase is eligible for tax exemption"
                  />
                </FormControl>
              </Box>
            </form>
          )}
          {step === 1 && tokenExpired && (
            <Alert severity="warning">
              Tax Exemption session has expired after 60 minutes. Please refresh
              the page to continue.
            </Alert>
          )}
          {step === 1 && (tokenQuery.isError || avalaraScript.isError) && (
            <ErrorLoadingAvalaraAlert />
          )}
          {step === 1 && (tokenQuery.isPending || avalaraScript.isLoading) && (
            <Box
              alignItems="center"
              display="flex"
              height="100%"
              justifyContent="center"
            >
              <PropagateLoader color={themeColor} loading={true} />
            </Box>
          )}
          {step === 1 &&
            tokenQuery.isSuccess &&
            avalaraScript.isLoaded &&
            state && (
              <AvalaraEcmPlugin
                onClose={onClose}
                onSuccess={handleCertificateUploaded}
                state={state}
                token={tokenQuery.data.token}
              />
            )}
          {step === 2 && (
            <>
              {projectTaxExemptionStatus === TAX_EXEMPTION_STATUSES.EXEMPT ? (
                <>
                  <Alert severity="success">
                    We&apos;ve verified your tax exemption certificate.
                  </Alert>
                  <Typography paddingTop={2} paragraph>
                    You may now complete your purchase. Your tax-exempt status
                    will automatically apply to all current and future orders.
                  </Typography>
                </>
              ) : (
                <>
                  <Alert severity="warning">
                    We&apos;ve received your tax exemption certificate. Reviews
                    typically take 1-3 business days.
                  </Alert>
                  <Typography paddingTop={2} paragraph>
                    We&apos;ll notify you by email when the review is complete.
                    You can also proceed with your purchase now. Once approved,
                    we&apos;ll process your tax refund automatically.
                  </Typography>
                </>
              )}
            </>
          )}
        </>
      </DialogContent>
      <DialogActions>
        <Stack
          gap={1}
          justifyContent="flex-end"
          sx={{
            flexDirection: { sm: 'column-reverse', md: 'row' },
            justifyContent: 'center',
            alignItems: { sm: 'stretch', md: 'end' },
            width: '100%',
          }}
        >
          <ChatWithUs />
          {!isInvalidState && step === 0 && (
            <Button
              data-testid="tax-exempt-continue-with-purchase-button"
              disabled={
                customer.isLoading ||
                customer.isError ||
                continueButtonLoading ||
                verificationMethod === ''
              }
              form="tax-exemptions-form"
              onClick={handleSubmit(handleContinue)}
              type="submit"
            >
              {continueButtonLoading ? (
                <CircularProgress color="secondary" size={23} />
              ) : (
                'Continue'
              )}
            </Button>
          )}
          {step === 2 && (
            <Button onClick={handleDialogClose} type="submit">
              Close
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
